'use client'
import { useMemo } from 'react'

const RELATIVE_WRAPPER_STYLE = Object.freeze({ position: 'relative' })

/**
 * Builds stable (useMemo'd) prop objects for every chrome component rendered by Layout.
 * Extracting these keeps Layout's render body focused on structure.
 */
export function useLayoutChromeProps({
  header,
  footer,
  pageConfig,
  pageDisclosure,
  secondaryDisclosures,
  consentBanner,
  specificHeaderCtaDataFiltered,
  footerSticky,
  showPhoneNumber,
  publicProcessDotEnv,
  disabledRoutes,
  disabledElements,
  showProgressPage,
  customPhoneData,
  dynamicStickyNav,
  predefinedChatPrompts,
  phoneData,
  printWebAppSchema,
  printLocalBusinessSchema,
  associatedProduct,
  isHomePath,
  isConsentBannerVisible,
  setConsentBannerVisible,
  chatbotVariation,
  isReturningUser,
  onChatbotFlagReady,
}) {
  const relativeWrapperStyle = RELATIVE_WRAPPER_STYLE

  const headerContainerProps = useMemo(
    () => ({
      content: header,
      siteEnv: header?.siteEnv,
      disabledRoutes,
      disabledElements,
      customPhoneData: customPhoneData ?? null,
      showProgressPage,
      specificHeaderCtaDataFiltered,
      footerSticky,
      dynamicStickyNav,
      showPhoneNumber,
      menuMobileLeft: header?.menuLeft,
      isHomePath,
      phoneData,
    }),
    [
      header,
      disabledRoutes,
      disabledElements,
      customPhoneData,
      showProgressPage,
      specificHeaderCtaDataFiltered,
      footerSticky,
      dynamicStickyNav,
      showPhoneNumber,
      isHomePath,
      phoneData,
    ]
  )

  const footerContainerProps = useMemo(
    () => ({
      disabledRoutes,
      content: footer,
      pageDisclosure: pageDisclosure || '',
      secondaryDisclosures,
      'data-testid': 'footer',
      showNav: header?.showNavFooter,
      showFooterSocialIcons: header?.showFooterSocialIcons,
      hiddenNavSection: pageConfig?.hiddenNavSection,
      printWebAppSchema,
      printLocalBusinessSchema,
    }),
    [
      disabledRoutes,
      footer,
      pageDisclosure,
      secondaryDisclosures,
      header?.showNavFooter,
      header?.showFooterSocialIcons,
      pageConfig?.hiddenNavSection,
      printWebAppSchema,
      printLocalBusinessSchema,
    ]
  )

  const consentBannerProps = useMemo(
    () => ({
      contentfulId: consentBanner?.sys?.id,
      contentfulVersion: consentBanner?.sys?.revision,
      siteEnv: header?.siteEnv,
      publicProcessDotEnv,
      onConsentBannerChange: setConsentBannerVisible,
    }),
    [
      consentBanner?.sys?.id,
      consentBanner?.sys?.revision,
      header?.siteEnv,
      publicProcessDotEnv,
      setConsentBannerVisible,
    ]
  )

  const footerStickyChatbotProps = useMemo(
    () => ({
      siteEnv: header?.siteEnv,
      predefinedChatPrompts,
      isConsentBannerVisible,
      associatedProduct,
      chatbotVariation,
      phoneData,
    }),
    [
      header?.siteEnv,
      predefinedChatPrompts,
      isConsentBannerVisible,
      associatedProduct,
      chatbotVariation,
      phoneData,
    ]
  )

  const footerStickyProps = useMemo(
    () => ({
      specificHeaderCtaDataFiltered,
      headerContent: header,
      content: footerSticky,
      dynamicStickyNav,
      phoneData,
    }),
    [specificHeaderCtaDataFiltered, header, footerSticky, dynamicStickyNav, phoneData]
  )

  const chatGateProps = useMemo(
    () => ({
      siteEnv: header?.siteEnv,
      isReturningUser,
      onFlagReady: onChatbotFlagReady,
    }),
    [header?.siteEnv, isReturningUser, onChatbotFlagReady]
  )

  return {
    relativeWrapperStyle,
    headerContainerProps,
    footerContainerProps,
    consentBannerProps,
    footerStickyChatbotProps,
    footerStickyProps,
    chatGateProps,
  }
}
